import axios, { AxiosInstance } from 'axios';
import { API_BASE_URL, SCORING_API_URL } from '../config/api';
import { normaliserErreurApi } from '../lib/apiError';

/**
 * Client du service de scoring en ligne (scoring.logfit.eu), pour la page
 * Jour J (2026-11-03).
 *
 * Pas de jeton en JavaScript : la session de cloud-admin est un cookie
 * HTTP-only sur .logfit.eu, que le navigateur envoie aussi au scoring
 * (`withCredentials`). Le scoring le vérifie avec le même secret que cloud-api.
 * Sur 401 (jeton d'accès expiré, 15 min), on le renouvelle auprès de cloud-api
 * puis on rejoue la requête, une fois.
 *
 * ⚠️ Tout se fait dans un événement donné : le scoring est partagé par tous les
 * organisateurs, il n'y a pas d'« événement actif » qui serait le nôtre.
 */

export type HeatRunStatus = 'ready' | 'preparing' | 'countdown' | 'running' | 'rest' | 'finished' | string;

export interface ScoringHeat {
  id: string;
  heat_number: number;
  heat_date: string | null;
  start_time: string | null;
  wod_id: string | null;
  wod_name: string | null;
  wod_number?: number | null;
  category_name: string | null;
  category_group_name: string | null;
  status: HeatRunStatus;
  max_participants?: number | null;
}

export interface HeatParticipant {
  team_id: string;
  team_name: string;
  lane_number: number;
}

export interface LaneValidation {
  lane_number: number;
  current_team_name: string | null;
  is_validated: boolean;
  judge_name: string | null;
}

export interface ValidationStatus {
  lanes: LaneValidation[];
  validated_count: number;
  required_count: number;
  all_ready: boolean;
}

export interface LiveScore {
  team_id: string;
  lane_number: number;
  reps_completed: number | null;
  rounds_completed: number | null;
  current_movement: string | null;
  finish_time: number | null;
  is_finished: boolean | null;
  announced_weight: number | null;
  is_successful: boolean | null;
  team_name: string;
}

export interface ConnectedDevice {
  device_id: string;
  device_name: string;
  reported_name: string;
  custom_name: string | null;
  lane_number: number | null;
  seconds_since_heartbeat: number | null;
  battery_level: number | null;
  battery_charging: boolean | null;
}

class ScoringService {
  private api: AxiosInstance;
  private refreshing: Promise<void> | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: SCORING_API_URL,
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });

    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;
        if (error.response?.status === 401 && original && !original._retry) {
          original._retry = true;
          try {
            await this.refreshSession();
            return this.api.request(original);
          } catch {
            // Session cloud perdue : l'intercepteur de cloud-admin s'en charge
            // au prochain appel à cloud-api ; ici on remonte l'erreur.
          }
        }
        return Promise.reject(normaliserErreurApi(error));
      },
    );
  }

  /** Un seul renouvellement à la fois : la page interroge en parallèle. */
  private refreshSession(): Promise<void> {
    if (!this.refreshing) {
      this.refreshing = axios
        .post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true })
        .then(() => undefined)
        .finally(() => { this.refreshing = null; });
    }
    return this.refreshing;
  }

  async activateEvent(eventId: string): Promise<void> {
    await this.api.post('/active-event', { event_id: eventId });
  }

  /** Code des juges, et si l'événement a une licence (sans elle, pas de heat en ligne). */
  async getJudgeAccess(eventId: string): Promise<{ access_code: string | null; license_active: boolean }> {
    const { data } = await this.api.get(`/events/${eventId}/judge-access`);
    return { access_code: data.access_code ?? null, license_active: data.license_active !== false };
  }

  async getHeats(eventId: string): Promise<ScoringHeat[]> {
    const { data } = await this.api.get('/heats', { params: { event_id: eventId } });
    return data.heats ?? [];
  }

  async getParticipants(heatId: string): Promise<HeatParticipant[]> {
    const { data } = await this.api.get(`/heats/${heatId}/participants`);
    return data.participants ?? [];
  }

  async getValidation(heatId: string): Promise<ValidationStatus> {
    const { data } = await this.api.get(`/heats/${heatId}/validation-status`);
    return data;
  }

  async getLiveScores(heatId: string): Promise<LiveScore[]> {
    const { data } = await this.api.get(`/heats/${heatId}/live-scores`);
    return data.live_scores ?? [];
  }

  /** État d'exécution en base (heure de départ pour le chrono). */
  async getHeatStatus(heatId: string): Promise<{ live_status: { status?: string; start_time?: string | null; countdown_start?: string | null } | null }> {
    const { data } = await this.api.get(`/heats/${heatId}/status`);
    return data;
  }

  /** Couloir « juge prêt » à la place d'un juge sans téléphone (préparation). */
  async validateLane(heatId: string, laneNumber: number): Promise<ValidationStatus> {
    const { data } = await this.api.post(`/heats/${heatId}/lanes/${laneNumber}/manual-validation`);
    return data;
  }

  async heatAction(heatId: string, action: 'prepare' | 'start' | 'stop' | 'reset'): Promise<void> {
    await this.api.post(`/heats/${heatId}/${action}`);
  }

  async getDevices(eventId: string): Promise<ConnectedDevice[]> {
    const { data } = await this.api.get('/public/devices/connected', { params: { event_id: eventId } });
    return data.devices ?? [];
  }

  async assignDevice(deviceId: string, laneNumber: number, eventId: string): Promise<void> {
    await this.api.post(`/devices/${encodeURIComponent(deviceId)}/assign`, {
      lane_number: laneNumber,
      event_id: eventId,
    });
  }

  async unassignDevice(deviceId: string): Promise<void> {
    await this.api.delete(`/devices/${encodeURIComponent(deviceId)}/assign`);
  }

  async identifyDevice(deviceId: string): Promise<void> {
    await this.api.post(`/devices/${encodeURIComponent(deviceId)}/identify`);
  }
}

export const scoringService = new ScoringService();
export const SCORING_HOST = new URL(SCORING_API_URL).host;
export const SCORING_PROTOCOL = new URL(SCORING_API_URL).protocol.replace(':', '');
/** Origine du scoring (sans /api/v1), là où il sert les écrans de salle. */
export const SCORING_ORIGIN = new URL(SCORING_API_URL).origin;
